import { useState, useEffect, useCallback } from "react";
import { groupSelectedOptions } from "../../../helpers/common";
import { paymentGroups, paymentOptions } from "../../../helpers/data-constants";

const usePaymentType = (
  form,
  maxTotalSum,
  inputFieldName,
  setCashChangePayment,
  setPaymentInputs
) => {
  const initialState = {
    cash: [],
    nonCash: [],
    erip: [],
  };

  const [paymentMethods, setPaymentMethods] = useState(initialState);
  const [amountFields, setAmountFields] = useState({});
  const [lastInputField, setLastInputField] = useState(null);
  const [isChanged, setIsChanged] = useState(false);
  const allInputs = [
    ...paymentMethods.cash,
    ...paymentMethods.nonCash,
    ...paymentMethods.erip,
  ];

  const calculateSum = useCallback((nextField) => {
      const { deposit = {} } = form.getValues();
      return allInputs.reduce(
        (total, { value }) =>
          total + (value !== nextField ? parseFloat(deposit[value]) || 0 : 0), 0)},
    [form, allInputs]
  );

  const calculateBalance = (totalUsed) => maxTotalSum - totalUsed.toFixed(2);

  const updateField = (name, value) => {
    setAmountFields((prev) => ({ ...prev, [name]: value }));
    form.setValue(`${inputFieldName}.${name}`, value);
  };

  const handleTypeDeal = (selectedOptions) => {
    setPaymentInputs(selectedOptions)
   
    const grouped = groupSelectedOptions(
      selectedOptions,
      paymentOptions,
      initialState
    );
    setPaymentMethods(grouped);
  };

  const handleInputChange = (name, value) => {
    const parsedValue = parseFloat(value) || 0;
    setAmountFields((prevValues) => ({ ...prevValues, [name]: parsedValue }));
    const hasCash = allInputs.some(({ value }) => value === paymentGroups.CASH);

    if (hasCash) {
      handleCashInputChange(name, parsedValue);
    
    } else {
      handleNonCashInputChange(name, parsedValue);
    }

    form.trigger();
  };

  const handleCashInputChange = (name, parsedValue) => {
    const totalUsed = calculateSum(paymentGroups.CASH);
    const remainingValue = calculateBalance(totalUsed);

    if (name !== paymentGroups.CASH) {
      const cashInput = allInputs[0].value;
      updateField(cashInput, remainingValue.toFixed(2));
      setIsChanged(true);

      if (totalUsed > maxTotalSum) {
        updateField(cashInput, 0);
      }
      updateCashChange(0);
    } else {
      const balance = calculateBalance(totalUsed).toFixed(2);
      const cashChange =  Math.max(0, parsedValue - balance);
    
      updateCashChange(cashChange.toFixed(2))
    }
  };

  const handleNonCashInputChange = (name, parsedValue) => {
    const fieldKeys = Object.keys(amountFields);
    const currentIndex = fieldKeys.indexOf(name);
    let nextField = getNextField(fieldKeys, name, currentIndex);
    const totalUsed = calculateSum(nextField);
    let remainingValue = calculateBalance(totalUsed);

    if (nextField) {
      amountFields[nextField] = remainingValue.toFixed(2);
    } else {
      nextField = fieldKeys.find((key) => key !== name);
      remainingValue = calculateBalance(parsedValue);
      amountFields[nextField] = remainingValue.toFixed(2);
    }

    setAmountFields(amountFields);
    setIsChanged(true);
    updateField(nextField, remainingValue.toFixed(2));
  };

  const handleInputBlur = (name) => {
    if (isChanged) {
      setLastInputField(name);
      setIsChanged(false);
    }
  };

  const getNextField = (fieldKeys, currentField, currentIndex) => {
    return fieldKeys.find((key, index) =>
        key !== currentField &&
        key !== lastInputField &&
        (lastInputField || index === (currentIndex + 1) % fieldKeys.length)
    );
  };

  const updateCashChange = (cashChangeValue) => {
    if (setCashChangePayment) {
      setCashChangePayment(cashChangeValue);
    }
  }

  useEffect(() => {
    setPaymentMethods((prev) => ({ ...prev, cash: [paymentOptions[0]] }));
    updateField(paymentGroups.CASH, maxTotalSum);
    setPaymentInputs(paymentOptions)
  }, []);

  useEffect(() => {
    const isSingleInput = allInputs.length === 1;
    const initialInputFields = {};

    Object.values(paymentMethods).flat().forEach(({ value }) => {
        const amountValue =
          value === paymentGroups.CASH || isSingleInput ? maxTotalSum || 0 : 0;

        initialInputFields[value] = amountValue;
        updateField(value, amountValue);
      });

    setAmountFields(initialInputFields);
    setLastInputField(null);
    updateCashChange(0)
    form.trigger();
  }, [paymentMethods, maxTotalSum]);

  return {
    paymentMethods,
    amountFields,
    handleTypeDeal,
    handleInputChange,
    calculateSum,
    handleInputBlur,
    calculateBalance,
    allInputs,
  };
};

export default usePaymentType;
